// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getApiPath, normalizeNumber, objectFetch } from 'util/commons';
import {
  getCollectRouteQRInfo,
  getRoutesCollectedTotalWeight,
  submitCollectRouteQR,
} from 'apis/collect_route';
import { QRCodeItemType } from 'types/routeManagementTypes';
import { Path } from 'routes';
import { toast } from 'react-toastify';
import Portal from 'components/atoms/Portal';
import { ReportAbnormalityModal } from '../ReportAbnormalityModal';
import { useSelector } from 'react-redux';
import { selectCollectingRouteSites } from 'redux/slices/collectingRoute';
import { useCheckRouteCollectProcess } from 'util/hooks/useCheckRouteCollectProcess';
import CollectEmissionsSelectDriverModal from '../CollectEmissionsSelectDriverModal';
import { selectUser } from 'redux/slices/userSlice';
import { RoleType } from 'util/Enums';
import { CollectingRouteSession, isRouteIdExist } from 'util/collectingRoute';
import QRScanning, {
  QRScanningProps,
  QRScanningSubmitParams,
} from 'components/organisms/QRScanning';

export type CollectRouteQRScanningConfirmParams = {
  someId: string; // TODO: update later
  onSuccess: () => void;
};

type CollectGroupItem = {
  id: string;
  weight: string;
};

const CollectRouteQRScanning = ({
  routeId,
  siteId,
  setHasCollectedSuccess,
  onClose,
  onConfirm,
  title,
  finishText,
}: {
  siteId: string;
  routeId?: string;
  setHasCollectedSuccess?: (value: boolean) => void;
  onClose?: () => void;
  onConfirm?: (params: CollectRouteQRScanningConfirmParams) => void;
  title?: string;
  finishText?: string;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useSelector(selectUser);
  const collectingRouteSites = useSelector(selectCollectingRouteSites);
  const { state } = useLocation();

  const [reportingTargetIds, setReportingTargetIds] = useState<string[] | null>(
    null
  );
  const [isShowDriverModal, setIsShowDriverModal] = useState<boolean>(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const pendingOpenConfirmModal = useRef<(() => void) | null>(null);

  useCheckRouteCollectProcess();

  const { areaId, areaName } = useMemo(() => {
    const currentSite = collectingRouteSites?.sites.find(
      (site) => site.id === siteId
    );
    return {
      areaId: currentSite?.area.id || '',
      areaName: currentSite?.area.name || '',
    };
  }, [collectingRouteSites, siteId]);

  const handleScan: QRScanningProps<
    QRCodeItemType,
    CollectGroupItem
  >['onScan'] = (qrCode, addItem) => {
    getCollectRouteQRInfo({
      siteId,
      qrCode,
      successCallback: (data) => {
        if (data && data.id) {
          toast.success(t('messages.M_136'));
          addItem(data);
        }
      },
    });
  };

  const handleSubmit = ({
    checkedItemIds,
    isCompleted,
    onSuccess,
  }: QRScanningSubmitParams<CollectGroupItem>) => {
    if (onConfirm) {
      onConfirm({ someId: 'test', onSuccess });
      return;
    }

    submitCollectRouteQR({
      routeId: isRouteIdExist(routeId) ? Number(routeId) : undefined,
      areaId: siteId,
      emissionIds: checkedItemIds,
      ...(!!selectedDriverId && { driverId: selectedDriverId }),
      successCallback: () => {
        toast.success(t('messages.M_087'));
        handleCloseDriverModal();
        onSuccess();
        CollectingRouteSession.addProcessedSite({
          routeId: routeId ?? '',
          siteId,
        });
        getRoutesCollectedTotalWeight();

        if (isCompleted) {
          history.push(
            `${getApiPath(Path.routeSiteSelection, {
              routeId: objectFetch(state, 'routeId') || routeId,
            })}${window.location.search}`,
            { refreshSites: true }
          );
        } else {
          setHasCollectedSuccess?.(true);
        }
      },
    });
  };

  const handleFinishButtonClick = ({
    openConfirmModal,
  }: {
    openConfirmModal: () => void;
  }) => {
    if (
      user.role === RoleType.AdminOperator ||
      user.role === RoleType.AdminSite
    ) {
      pendingOpenConfirmModal.current = openConfirmModal;
      setIsShowDriverModal(true);
    } else {
      openConfirmModal();
    }
  };

  const handleDriverSubmit = (driverId: string) => {
    setSelectedDriverId(driverId);
    setIsShowDriverModal(false);
    pendingOpenConfirmModal.current?.();
    pendingOpenConfirmModal.current = null;
  };

  const handleCloseDriverModal = () => {
    setSelectedDriverId('');
    setIsShowDriverModal(false);
    pendingOpenConfirmModal.current = null;
  };

  return (
    <>
      <QRScanning
        onScan={handleScan}
        getItemId={(data) => data.id}
        getGroupId={(data) => data.wasteUnit.id.toString()}
        getGroupName={(data) => data.wasteUnit.name}
        toGroupItem={(data) => ({ id: data.id, weight: data.weight })}
        getLatestScanDisplay={(data) => ({
          label: t('qr_scan.item_name') as string,
          name: data.wasteUnit.name,
          value: data.weight,
          unit: 'kg',
        })}
        getGroupItemDisplay={(item) => ({
          value: normalizeNumber({ value: item.weight }),
          unit: 'kg',
        })}
        getGroupSummaryDisplay={({ group, checkedItemIds }) => {
          const total = group.data.reduce((sum, item) => {
            if (!checkedItemIds.includes(item.id)) return sum;
            return sum + (parseFloat(item.weight) || 0);
          }, 0);
          return {
            value: normalizeNumber({ value: total.toString() }),
            unit: 'kg',
          };
        }}
        getConfirmData={({ checkedItemIds, groupedItems }) => {
          let totalWeight = 0;
          const items: Record<string, { name: string; weight: string }> = {};

          groupedItems.forEach((group) => {
            const checked = group.data.filter((item) =>
              checkedItemIds.includes(item.id)
            );
            if (checked.length > 0) {
              const w = checked.reduce(
                (sum, item) => sum + (parseFloat(item.weight) || 0),
                0
              );
              totalWeight += w;
              items[group.id] = { name: group.name, weight: w.toString() };
            }
          });

          return { quantity: checkedItemIds.length, totalWeight, items };
        }}
        renderGroupAction={(group) => (
          <button
            className="reportAbnormalButton"
            onClick={() =>
              setReportingTargetIds(group.data.map((item) => item.id))
            }
          >
            {t('confirm_select_route_item.mark_abnormal')}
          </button>
        )}
        onSubmit={handleSubmit}
        onFinishButtonClick={handleFinishButtonClick}
        onClose={onClose}
        title={title}
        finishText={finishText}
        paused={reportingTargetIds !== null}
        showConfirmModal
      />

      {isShowDriverModal && (
        <CollectEmissionsSelectDriverModal
          closeModal={handleCloseDriverModal}
          onSubmit={handleDriverSubmit}
          routeId={routeId}
        />
      )}

      {reportingTargetIds && reportingTargetIds.length > 0 && (
        <Portal>
          <ReportAbnormalityModal
            area={{ name: areaName, id: areaId }}
            targetIds={reportingTargetIds}
            onclose={() => setReportingTargetIds(null)}
          />
        </Portal>
      )}
    </>
  );
};

export default CollectRouteQRScanning;
